import React, { useEffect } from 'react';

import { ThemedCard } from '../components/themed/index.js';
import { useAppDispatch } from '../lib/reduxHooks.js';
import { initializeAuth } from '../store/slices/auth.js';

import OffsetContent from './OffsetContent.js';

const RETRY_INTERVAL_MS = 10_000;

/**
 * Shown when GET /auth/me could not be reached — the auth slice's
 * `unavailable` status.
 *
 * This is deliberately NOT a logout screen. The session lives in an HttpOnly
 * cookie the client can't inspect, so "the server didn't answer" tells us
 * nothing about whether the user is signed in. The overwhelmingly common cause
 * is a deploy swapping containers for a few seconds. Bouncing to /login here
 * would throw away a valid session and force a real re-login — which is
 * exactly the bug this screen exists to prevent.
 *
 * It heals itself: it re-checks on an interval and the moment the browser
 * reports it is back online, so a user who wandered off during a deploy comes
 * back to the app already restored.
 */
export const AuthUnavailableNotice: React.FC = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const retry = () => { void dispatch(initializeAuth()); };
    const timer = setInterval(retry, RETRY_INTERVAL_MS);
    window.addEventListener('online', retry);
    return () => {
      clearInterval(timer);
      window.removeEventListener('online', retry);
    };
  }, [dispatch]);

  return (
    <OffsetContent offsetRatio={0.1}>
      <ThemedCard className="p-8">
        <div className="text-center space-y-3">
          <div className="text-lg">Reconnecting…</div>
          <div className="text-sm opacity-70">
            We couldn&apos;t reach the server. You&apos;re still signed in — this usually
            clears up within a few seconds.
          </div>
          <button
            type="button"
            className="underline text-sm"
            onClick={() => { void dispatch(initializeAuth()); }}
          >
            Try again now
          </button>
        </div>
      </ThemedCard>
    </OffsetContent>
  );
};

export default AuthUnavailableNotice;
